import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getService } from "../../reducers/service";
import { serviceNames } from "../../utils/constants";
import { getParticipantsByCourseNumber } from "../../reducers/TrainingParticipantReducer";
import {
  endOfLastWeek,
  endOfNextWeek,
  endOfThisWeek,
  getTotal,
  isBetween,
  last30DaysDate,
  next30DaysDate,
  startOfLastWeek,
  startOfNextWeek,
  startOfThisWeek,
  today,
} from "../../utils/functions";
import { CountTable } from "../../components/CountTable";

const PageMain = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const userAgency = useSelector((state) => state.user.userAgency);
  const [loading, setLoading] = useState(true);
  const [applicantsByStatus, setApplicantsByStatus] = useState({});
  const [departmentCounts, setDepartmentCounts] = useState({});
  const [sectionCounts, setSectionCounts] = useState({});
  const [rankCounts, setRankCounts] = useState({});
  const [titleCounts, setTitleCounts] = useState({});
  const [courseWeekCategoryCounts, setCourseWeekCategoryCounts] = useState({
    thisWeek: {},
    lastWeek: {},
    nextWeek: {},
  });
  const [participantWeekNameCounts, setParticipantWeekNameCounts] = useState({
    thisWeek: {},
    lastWeek: {},
    nextWeek: {},
  });
  const [certificationCounts, setCertificationCounts] = useState({
    next30Days: {},
    last30Days: {},
    current: {},
  });
  const [participantWeekCounts, setParticipantWeekCounts] = useState({
    thisWeek: {},
    lastWeek: {},
    nextWeek: {},
  });

  const fetchApplicants = async () => {
    const service = getService(serviceNames.perApplicant);
    service.timeout = 20000;
    const res = await service.find({
      query: { $limit: -1, agencyId: userAgency, isDeleted: false },
    });
    if (res) {
      const grouped = res.reduce((acc, applicant) => {
        const status = applicant.status || "N/A";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      setApplicantsByStatus(grouped);
    }
  };

  const fetchEmployees = async () => {
    const service = getService(serviceNames.personnel);
    service.timeout = 20000;
    const res = await service.find({
      query: { $limit: -1, agencyId: userAgency, isDeleted: false },
    });
    if (res) {
      const deptGrouped = res.reduce((acc, emp) => {
        const dept = emp.department || "N/A";
        acc[dept] = (acc[dept] || 0) + 1;
        return acc;
      }, {});
      const sectionGrouped = res.reduce((acc, emp) => {
        const section = emp.section || "N/A";
        acc[section] = (acc[section] || 0) + 1;
        return acc;
      }, {});
      const rankGrouped = res.reduce((acc, emp) => {
        const rank = emp.rank || "N/A";
        acc[rank] = (acc[rank] || 0) + 1;
        return acc;
      }, {});
      const titleGrouped = res.reduce((acc, emp) => {
        const title = emp.titlePosition || "N/A";
        acc[title] = (acc[title] || 0) + 1;
        return acc;
      }, {});
      setDepartmentCounts(deptGrouped);
      setSectionCounts(sectionGrouped);
      setRankCounts(rankGrouped);
      setTitleCounts(titleGrouped);
    }
  };

  const fetchCourses = async () => {
    const service = getService(serviceNames.trainingCourse);
    service.timeout = 20000;
    const res = await service.find({
      query: { $limit: -1, agencyId: userAgency, isDeleted: false },
    });

    const part = await dispatch(
      getParticipantsByCourseNumber(res?.courseNumber)
    );

    if (res && part?.participants) {
      let weekCategoryCounts = {
        thisWeek: {},
        lastWeek: {},
        nextWeek: {},
      };

      res.forEach((course) => {
        if (!course.courseBeginDate) return;
        const begin = moment(course.courseBeginDate);
        const cat = course.category || "N/A";

        if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
          weekCategoryCounts.thisWeek[cat] =
            (weekCategoryCounts.thisWeek[cat] || 0) + 1;
        } else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
          weekCategoryCounts.lastWeek[cat] =
            (weekCategoryCounts.lastWeek[cat] || 0) + 1;
        } else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
          weekCategoryCounts.nextWeek[cat] =
            (weekCategoryCounts.nextWeek[cat] || 0) + 1;
        }
      });

      setCourseWeekCategoryCounts(weekCategoryCounts);

      let weekParticipantCounts = {
        thisWeek: {},
        lastWeek: {},
        nextWeek: {},
      };

      part.participants.forEach((p) => {
        const courseObj = res.find((c) => c.courseName === p.course);
        if (!courseObj?.courseBeginDate) return;

        const begin = moment(courseObj.courseBeginDate);
        const courseName = p.course || "N/A";

        if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
          weekParticipantCounts.thisWeek[courseName] =
            (weekParticipantCounts.thisWeek[courseName] || 0) + 1;
        } else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
          weekParticipantCounts.lastWeek[courseName] =
            (weekParticipantCounts.lastWeek[courseName] || 0) + 1;
        } else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
          weekParticipantCounts.nextWeek[courseName] =
            (weekParticipantCounts.nextWeek[courseName] || 0) + 1;
        }
      });

      setParticipantWeekCounts(weekParticipantCounts);
    }
  };

  const fetchParticipant = async () => {
    const service = getService(serviceNames.perTraining);
    service.timeout = 20000;
    const res = await service.find({
      query: { $limit: -1, agencyId: userAgency, isDeleted: false },
    });
    if (res) {
      let weekCategoryCounts = {
        thisWeek: {},
        lastWeek: {},
        nextWeek: {},
      };

      res.forEach((participant) => {
        if (!participant.attendedDate) return;

        const begin = moment(participant.attendedDate);
        const participantName = participant?.employeeDetails
          ? `${participant.employeeDetails.firstName || ""} ${
              participant.employeeDetails.middleName || ""
            } ${participant.employeeDetails.lastName || ""}`.trim()
          : "N/A";

        const attendedHours = participant.hours || 0;

        if (isBetween(begin, startOfThisWeek, endOfThisWeek)) {
          weekCategoryCounts.thisWeek[participantName] =
            (weekCategoryCounts.thisWeek[participantName] || 0) + attendedHours;
        } else if (isBetween(begin, startOfLastWeek, endOfLastWeek)) {
          weekCategoryCounts.lastWeek[participantName] =
            (weekCategoryCounts.lastWeek[participantName] || 0) + attendedHours;
        } else if (isBetween(begin, startOfNextWeek, endOfNextWeek)) {
          weekCategoryCounts.nextWeek[participantName] =
            (weekCategoryCounts.nextWeek[participantName] || 0) + attendedHours;
        }
      });

      setParticipantWeekNameCounts(weekCategoryCounts);
    }
  };

  const fetchCertificate = async () => {
    const service = getService(serviceNames.perTrainingCertification);
    service.timeout = 20000;
    const res = await service.find({
      query: { $limit: -1, agencyId: userAgency, isDeleted: false },
    });

    if (res) {
      let weekCategoryCounts = {
        next30Days: {},
        last30Days: {},
        current: {},
      };

      res.forEach((cert) => {
        if (!cert.expireDate) return;
        const expire = moment(cert.expireDate);
        const certType = cert.certificationType || "N/A";

        if (expire >= today && expire <= next30DaysDate) {
          weekCategoryCounts.next30Days[certType] =
            (weekCategoryCounts.next30Days[certType] || 0) + 1;
        } 
        if (expire < today && expire >= last30DaysDate) {
          weekCategoryCounts.last30Days[certType] =
            (weekCategoryCounts.last30Days[certType] || 0) + 1;
        } 
        if (expire >= today) {
          weekCategoryCounts.current[certType] =
            (weekCategoryCounts.current[certType] || 0) + 1;
        }
      });

      setCertificationCounts(weekCategoryCounts);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        await Promise.all([
          fetchApplicants(),
          fetchEmployees(),
          fetchCourses(),
          fetchParticipant(),
          fetchCertificate(),
        ]);
      } catch (error) {
        console.error("Error while loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [userAgency]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "transparent",
        }}
      >
        <CircularProgress
          size={48}
          thickness={4}
          sx={{
            color: "white",
            mb: 2,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, overflowY: "auto", maxHeight: "90vh", mt: 1 }}>
      <Typography
        variant="h2"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 4,
          color: theme.palette.colorFont,
        }}
      >
        Asset Management Home Page
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: "calc(100vh - 180px)",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Applicants
            </Typography>
            <Divider sx={{ my: 1, bgcolor: "black" }} />
            <Box sx={{ overflowY: "auto" }}>
              {
                <CountTable
                  data={applicantsByStatus}
                  label={"Status"}
                  showHours={null}
                  autoHeight={true}
                />
              }
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: "calc(100vh - 180px)",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Employees
            </Typography>
            <Divider sx={{ my: 1, bgcolor: "black" }} />
            <Box sx={{ overflowY: "auto" }}>
              {<CountTable data={departmentCounts} label={"Department"} />}

              {<CountTable data={sectionCounts} label={"Section"} />}

              {<CountTable data={rankCounts} label={"Rank"} />}

              {<CountTable data={titleCounts} label={"Title"} />}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: "calc(100vh - 180px)",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Training
            </Typography>
            <Divider sx={{ my: 1, bgcolor: "black" }} />

            <Grid container sx={{ flexGrow: 1, height: "100%" }}>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  pr: 2,
                  borderRight: "1px solid #ccc",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", fontSize: "17px" }}
                >
                  Courses
                </Typography>
                <Divider sx={{ my: 1, bgcolor: "black" }} />

                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  This Week (
                  {Object.keys(courseWeekCategoryCounts.thisWeek).length})
                </Typography>
                {
                  <CountTable
                    data={courseWeekCategoryCounts.thisWeek}
                    label={"Category"}
                  />
                }

                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", mt: 1 }}
                >
                  Participants By Course (
                  {getTotal(participantWeekCounts.thisWeek)})
                </Typography>
                {
                  <CountTable
                    data={participantWeekCounts.thisWeek}
                    label={"Course Name"}
                  />
                }

                <Divider sx={{ my: 1, mt: 2, bgcolor: "black" }} />

                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 2 }}>
                  Last Week (
                  {Object.keys(courseWeekCategoryCounts.lastWeek).length})
                </Typography>
                {
                  <CountTable
                    data={courseWeekCategoryCounts.lastWeek}
                    label={"Category"}
                  />
                }

                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", mt: 1 }}
                >
                  Participants By Course (
                  {getTotal(participantWeekCounts.lastWeek)})
                </Typography>
                {
                  <CountTable
                    data={participantWeekCounts.lastWeek}
                    label={"Course Name"}
                  />
                }

                <Divider sx={{ my: 1, mt: 2, bgcolor: "black" }} />

                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 2 }}>
                  Next Week (
                  {Object.keys(courseWeekCategoryCounts.nextWeek).length})
                </Typography>
                {
                  <CountTable
                    data={courseWeekCategoryCounts.nextWeek}
                    label={"Category"}
                  />
                }

                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", mt: 1 }}
                >
                  Participants By Course (
                  {getTotal(participantWeekCounts.nextWeek)})
                </Typography>
                {
                  <CountTable
                    data={participantWeekCounts.nextWeek}
                    label={"Course Name"}
                  />
                }
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  px: 2,
                  borderRight: "1px solid #ccc",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mt: isSmall ? 2 : null,
                    fontSize: "17px",
                  }}
                >
                  Participants
                </Typography>
                <Divider sx={{ my: 1, bgcolor: "black" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  This Week (
                  {Object.keys(participantWeekNameCounts.thisWeek).length})
                </Typography>
                {
                  <CountTable
                    data={participantWeekNameCounts.thisWeek}
                    label={"Name"}
                    showHours={true}
                  />
                }
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  Last Week (
                  {Object.keys(participantWeekNameCounts.lastWeek).length})
                </Typography>
                {
                  <CountTable
                    data={participantWeekNameCounts.lastWeek}
                    label={"Name"}
                    showHours={true}
                  />
                }
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  Next Week (
                  {Object.keys(participantWeekNameCounts.nextWeek).length})
                </Typography>
                {
                  <CountTable
                    data={participantWeekNameCounts.nextWeek}
                    label={"Name"}
                    showHours={true}
                  />
                }
              </Grid>

              <Grid item xs={12} md={4} sx={{ pl: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mt: isSmall ? 2 : null,
                    fontSize: "17px",
                  }}
                >
                  Certifications
                </Typography>
                <Divider sx={{ my: 1, bgcolor: "black" }} />
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  Expiring Next 30 Days
                </Typography>
                {
                  <CountTable
                    data={certificationCounts.next30Days}
                    label={"Certificate Type"}
                  />
                }
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  Expired Last 30 Days
                </Typography>
                {
                  <CountTable
                    data={certificationCounts.last30Days}
                    label={"Certificate Type"}
                  />
                }
                <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                  Current
                </Typography>
                {
                  <CountTable
                    data={certificationCounts.current}
                    label={"Certificate Type"}
                  />
                }
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PageMain;
